/**
 * Mobile Header Component
 *
 * Simple header with logo and user avatar for mobile app.
 * Respects safe area insets for notched devices.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Settings, LogOut, Shield, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ContentSageIcon } from '../ContentSageLogo';

interface MobileHeaderProps {
  onSettingsClick?: () => void;
  onMFAClick?: () => void;
}

export const MobileHeader: React.FC<MobileHeaderProps> = ({
  onSettingsClick,
  onMFAClick,
}) => {
  const { user, signOutUser } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOutUser();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-100 pt-[env(safe-area-inset-top)]">
      <div className="flex items-center justify-between h-14 px-4">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <ContentSageIcon size={32} />
          <span className="text-lg font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-amber-400 bg-clip-text text-transparent">
            ContentSage
          </span>
        </div>

        {/* User Avatar */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 p-1.5 rounded-full hover:bg-gray-100 transition touch-target"
          >
            {user?.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName || 'User'}
                className="w-9 h-9 rounded-full border-2 border-purple-100"
              />
            ) : (
              <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center border-2 border-purple-200">
                <User className="w-5 h-5 text-purple-600" />
              </div>
            )}
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>

          {/* User Dropdown */}
          <AnimatePresence>
            {showUserMenu && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-40"
                  onClick={() => setShowUserMenu(false)}
                />

                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50"
                >
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.displayName || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>

                  <div className="py-1">
                    {/* Settings */}
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        onSettingsClick?.();
                      }}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full text-left touch-target"
                    >
                      <Settings className="w-5 h-5 text-gray-400" />
                      Settings
                    </button>

                    {/* MFA Setup */}
                    {!user?.mfaEnabled && (
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          onMFAClick?.();
                        }}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full text-left touch-target"
                      >
                        <Shield className="w-5 h-5 text-amber-500" />
                        Enable Two-Factor Auth
                      </button>
                    )}
                  </div>

                  {/* Sign Out */}
                  <div className="border-t border-gray-100 pt-1">
                    <button
                      onClick={handleSignOut}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 w-full text-left touch-target"
                    >
                      <LogOut className="w-5 h-5" />
                      Sign Out
                    </button>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default MobileHeader;
